import { lazy } from 'react';
import WithSuspense from '../../components/Loaders/WithSuspense';
import { AppRoute } from '../types';
import { PRIVATE_PATHS } from '../constants';
import { useAppSelector } from '../../store';

const { HRMS } = PRIVATE_PATHS;

// Admin pages
const ViewEmployees = WithSuspense(
  lazy(
    () => import('../../pages/HRMS/Admin/EmployeeDirectory/ViewAllEmployee'),
  ),
);
const EmployeeOnboardingWizard = WithSuspense(
  lazy(
    () =>
      import('../../pages/HRMS/Admin/EmployeeDirectory/EmployeeOnboardingWizard'),
  ),
);
const ViewEmployeeDetails = WithSuspense(
  lazy(
    () =>
      import('../../pages/HRMS/Admin/EmployeeDirectory/ViewEmployeeDetails'),
  ),
);
const Attendance = WithSuspense(
  lazy(() => import('../../pages/HRMS/Admin/Attendance/Attendance')),
);
const ProfileTrackerList = WithSuspense(
  lazy(
    () => import('../../pages/HRMS/Admin/ProfileTracker/ProfileTrackerList'),
  ),
);
const ProfileTrackerReview = WithSuspense(
  lazy(
    () => import('../../pages/HRMS/Admin/ProfileTracker/ProfileTrackerReview'),
  ),
);
const ExitMgt = WithSuspense(
  lazy(() => import('../../pages/HRMS/Admin/ExitMgt/ExitMgt')),
);
const ViewEmployeeExitProcess = WithSuspense(
  lazy(() => import('../../pages/HRMS/Admin/ExitMgt/ViewExitProcess')),
);
const LeaveManagement = WithSuspense(
  lazy(() => import('../../pages/HRMS/Admin/LeaveMgt/LeaveMgt')),
);
const LeaveRequest = WithSuspense(
  lazy(() => import('../../pages/HRMS/Admin/LeaveMgt/LeaveRequests')),
);
const LeaveRequestDetail = WithSuspense(
  lazy(() => import('../../pages/HRMS/Admin/LeaveMgt/LeaveRequestDetail')),
);
const Repository = WithSuspense(
  lazy(() => import('../../pages/HRMS/Admin/Repository/Repository')),
);

// Settings
const SettingsLayout = WithSuspense(
  lazy(() => import('../../pages/HRMS/Settings/SettingsLayout')),
);
const LeaveAllocation = WithSuspense(
  lazy(() => import('../../pages/HRMS/Settings/Tabs/LeaveAllocation')),
);
const AttendanceRules = WithSuspense(
  lazy(() => import('../../pages/HRMS/Settings/Tabs/AttendanceRules')),
);
const AttendancePolicyConfig = WithSuspense(
  lazy(() => import('../../pages/HRMS/Settings/Tabs/AttendancePolicyConfig')),
);
const Departments = WithSuspense(
  lazy(() => import('../../pages/HRMS/Settings/Tabs/Departments')),
);
const Designations = WithSuspense(
  lazy(() => import('../../pages/HRMS/Settings/Tabs/Designations')),
);
const HolidayCalendars = WithSuspense(
  lazy(() => import('../../pages/HRMS/Settings/Tabs/HolidayCalendars')),
);
const HolidayCalendarDetail = WithSuspense(
  lazy(() => import('../../pages/HRMS/Settings/Tabs/HolidayCalendarDetail')),
);
const Workflows = WithSuspense(
  lazy(() => import('../../pages/HRMS/Settings/Tabs/Workflows')),
);
const WorkflowBuilder = WithSuspense(
  lazy(() => import('../../pages/HRMS/Settings/Tabs/WorkflowBuilder')),
);

// Employee pages
const EmployeeCalendar = WithSuspense(
  lazy(() => import('../../pages/HRMS/Employee/Attendance/Attendance')),
);
const EmployeeLeaveMgt = WithSuspense(
  lazy(() => import('../../pages/HRMS/Employee/LeaveMgt/LeaveMgt')),
);

const AdminDashboard = WithSuspense(
  lazy(() => import('../../pages/HRMS/Admin/Dashboard/Dashboard')),
);
const EmployeeDashboard = WithSuspense(
  lazy(() => import('../../pages/HRMS/Employee/Dashboard/Dashboard')),
);

const ADMIN_ROLES = ['ORG_OWNER', 'ORG_ADMIN', 'HRM_MANAGER', 'HRM_ADMIN'];

const SETTINGS_ROLES = ['ORG_OWNER', 'ORG_ADMIN', 'HRM_ADMIN'];

const EMPLOYEE_ROLES = [
  'EMPLOYEE',
  'ORG_OWNER',
  'ORG_ADMIN',
  'HRM_ADMIN',
  'HRM_MANAGER',
  'HRM_EMPLOYEE',
  'PM_ADMIN',
];

const HRMSDashboard = () => {
  const roles = useAppSelector((state: any) => state.auth.roles);
  const isAdmin = roles.some((role: string) => ADMIN_ROLES.includes(role));

  console.log('roles', roles);
  return isAdmin ? <AdminDashboard /> : <EmployeeDashboard />;
};

const HRMSNotifications = WithSuspense(
  lazy(() => import('../../pages/HRMS/Notifications/Notifications')),
);

const HRMSAttendance = () => {
  const roles = useAppSelector((state: any) => state.auth.roles);
  const isAdmin = roles.some((role: string) => ADMIN_ROLES.includes(role));

  return isAdmin ? <Attendance /> : <EmployeeCalendar />;
};

const HRMSLeave = () => {
  const roles = useAppSelector((state: any) => state.auth.roles);
  const isAdmin = roles.some((role: string) => ADMIN_ROLES.includes(role));

  return isAdmin ? <LeaveManagement /> : <EmployeeLeaveMgt />;
};

export const HRMS_MODULE_ROUTES: AppRoute[] = [
  // Dashboard
  { path: HRMS.DASHBOARD, element: <HRMSDashboard />, roles: EMPLOYEE_ROLES },

  // Notifications
  {
    path: HRMS.NOTIFICATIONS,
    element: <HRMSNotifications />,
    roles: EMPLOYEE_ROLES,
  },

  // Employee Directory (Admin only)
  {
    path: HRMS.EMPLOYEES.VIEW_ALL,
    element: <ViewEmployees />,
    roles: ADMIN_ROLES,
  },
  {
    path: HRMS.EMPLOYEES.ADD,
    element: <EmployeeOnboardingWizard />,
    roles: ADMIN_ROLES,
  },
  {
    path: HRMS.EMPLOYEES.EDIT,
    element: <EmployeeOnboardingWizard />,
    roles: ADMIN_ROLES,
  },
  {
    path: HRMS.EMPLOYEES.DETAILS,
    element: <ViewEmployeeDetails />,
    roles: ADMIN_ROLES,
  },

  // Attendance
  {
    path: HRMS.ATTENDANCE.MAIN,
    element: <HRMSAttendance />,
    roles: EMPLOYEE_ROLES,
  },
  {
    path: HRMS.ATTENDANCE.CALENDAR,
    element: <EmployeeCalendar />,
    roles: EMPLOYEE_ROLES,
  },
  {
    path: HRMS.ATTENDANCE.REGULARIZATION_REQUESTS,
    element: <Attendance />,
    roles: ADMIN_ROLES,
  },

  // Leave Management
  { path: HRMS.LEAVE.MAIN, element: <HRMSLeave />, roles: EMPLOYEE_ROLES },
  { path: HRMS.LEAVE.REQUESTS, element: <LeaveRequest />, roles: ADMIN_ROLES },
  {
    path: HRMS.LEAVE.REQUEST_DETAIL,
    element: <LeaveRequestDetail />,
    roles: EMPLOYEE_ROLES,
  },

  // Leave (Employee specific)
  {
    path: HRMS.LEAVE.APPLY,
    element: <EmployeeLeaveMgt />,
    roles: EMPLOYEE_ROLES,
  },
  {
    path: HRMS.LEAVE.BALANCE,
    element: <EmployeeLeaveMgt />,
    roles: EMPLOYEE_ROLES,
  },
  {
    path: HRMS.LEAVE.HISTORY,
    element: <EmployeeLeaveMgt />,
    roles: EMPLOYEE_ROLES,
  },

  // My Workspace (Self-Service for Admins)
  {
    path: HRMS.MY_ATTENDANCE,
    element: <EmployeeCalendar />,
    roles: ADMIN_ROLES,
  },
  { path: HRMS.MY_LEAVE, element: <EmployeeLeaveMgt />, roles: ADMIN_ROLES },

  // Profile Tracker (Admin)
  {
    path: HRMS.PROFILE_TRACKER.MAIN,
    element: <ProfileTrackerList />,
    roles: ADMIN_ROLES,
  },
  {
    path: HRMS.PROFILE_TRACKER.REVIEW,
    element: <ProfileTrackerReview />,
    roles: ADMIN_ROLES,
  },

  // Exit Management (Admin)
  { path: HRMS.EXIT_MGT.MAIN, element: <ExitMgt />, roles: ADMIN_ROLES },
  {
    path: HRMS.EXIT_MGT.PROCESS,
    element: <ViewEmployeeExitProcess />,
    roles: ADMIN_ROLES,
  },

  // Repository (Admin)
  { path: HRMS.REPOSITORY.MAIN, element: <Repository />, roles: ADMIN_ROLES },

  // Settings
  {
    path: HRMS.SETTINGS.MAIN,
    element: <SettingsLayout />,
    roles: SETTINGS_ROLES,
    children: [
      { path: HRMS.SETTINGS.LEAVE_ALLOCATION, element: <LeaveAllocation /> },
      { path: HRMS.SETTINGS.ATTENDANCE_RULES, element: <AttendanceRules /> },
      {
        path: HRMS.SETTINGS.ATTENDANCE_RULES_DETAILS,
        element: <AttendancePolicyConfig />,
      },
      { path: HRMS.SETTINGS.DEPARTMENTS, element: <Departments /> },
      {
        path: HRMS.SETTINGS.DEPARTMENTS_DESIGNATIONS,
        element: <Designations />,
      },
      {
        path: HRMS.SETTINGS.HOLIDAY_CALENDARS,
        element: <HolidayCalendars />,
      },
      {
        path: HRMS.SETTINGS.HOLIDAY_CALENDARS_DETAILS,
        element: <HolidayCalendarDetail />,
      },
      { path: HRMS.SETTINGS.WORKFLOWS, element: <Workflows /> },
      { path: HRMS.SETTINGS.WORKFLOWS_NEW, element: <WorkflowBuilder /> },
      { path: HRMS.SETTINGS.WORKFLOWS_EDIT, element: <WorkflowBuilder /> },
    ],
  },
];
